import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { newsAPI } from '../services/api';
import {
  AwardIcon, FileCheckIcon, ShieldCheckIcon, StarIcon,
  LockIcon, BanknoteIcon, AlertCircleIcon, TrendingUpIcon,
  BoltIcon, CpuIcon, MonitorIcon, SendIcon,
  CheckCircleIcon, XCircleIcon, MinusCircleIcon
} from './HomeIcons';
import './Home.css';

const StatusIcon = ({ status }) => {
  if (status === 'yes') return <CheckCircleIcon className="status-icon status-yes" />;
  if (status === 'no') return <XCircleIcon className="status-icon status-no" />;
  return <MinusCircleIcon className="status-icon status-dash" />;
};

/* 数字滚动动画：数值从 0 滚到目标值，非数字文本直接显示 */
const StatValue = ({ value, active }) => {
  const match = /^(\d+)(.*)$/.exec(value);
  const [display, setDisplay] = useState(match ? `0${match[2]}` : value);

  useEffect(() => {
    if (!match) {
      setDisplay(value);
      return;
    }
    if (!active) return;
    const target = parseInt(match[1], 10);
    const suffix = match[2];
    const duration = 1400;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(`${Math.round(eased * target)}${suffix}`);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, value]);

  return <>{display}</>;
};

const Home = () => {
  const [newsList, setNewsList] = useState([]);
  const [painInView, setPainInView] = useState(false);
  const [statsInView, setStatsInView] = useState(false);
  const painGridRef = useRef(null);
  const statsRef = useRef(null);

  const productHighlights = [
    { number: '2011', label: '年成立，持续服务至今' },
    { number: '10+', label: '年政企服务经验' },
    { number: '唯一', label: 'WAIC2024私有化实机参展' },
    { number: '100%', label: '数据本地存储，永不出网' }
  ];

  const credentialCards = [
    {
      icon: AwardIcon,
      title: '国家王选新闻科技技术奖',
      desc: '以中国计算机汉字激光照排创始人命名的国家级科技大奖，我们的技术经过了国家最高级别的认可。'
    },
    {
      icon: FileCheckIcon,
      title: '国家行业标准制定者',
      desc: '参与国家行业标准 SJ/T 11615.2-2016 的制定，行业的规则是我们参与写的。'
    },
    {
      icon: ShieldCheckIcon,
      title: '权威安全认证全覆盖',
      desc: '华为鲲鹏技术认证、海光适配认证，国产芯片、信创环境完美兼容，不用操心适配问题。'
    },
    {
      icon: StarIcon,
      title: 'WAIC2024唯一实机参展',
      desc: '世界人工智能大会上，唯一以私有化部署实机参展的企业，产品经得起公开检验。'
    }
  ];

  const clientTags = [
    '国家级安全机关', '公安部直属研究所', '直辖市网信主管部门', '大型航空制造央企',
    '全国性证券机构', '大型建工集团', '直辖市委组织部门', '省级公安机关',
    '部队及军工单位', '省级产业研究院'
  ];

  const painPoints = [
    {
      icon: LockIcon,
      title: '不敢用',
      desc: '用公网AI，合同、人事数据要上传到别人的服务器。数据一旦外泄，后果不是效率问题，而是合规风险。',
      solution: '我们的AI装在您自己的机房里，数据从始至终不出门。'
    },
    {
      icon: BanknoteIcon,
      title: '用不起',
      desc: '自己建AI团队，算力设备几十万、工程师年薪几十万、开发周期半年起。算下来百万起步，预算批不下来。',
      solution: '年度订阅服务，费用远低于自建成本，算力硬件由我们提供部署，无需单独采购。'
    },
    {
      icon: AlertCircleIcon,
      title: '用不好',
      desc: '员工不会用AI，免费大模型只支持十几种格式，不支持WPS，处理不了合同PDF和会议录音。',
      solution: '内置几十种办公场景智能体，100+种格式通吃，说句话就能用。'
    },
    {
      icon: TrendingUpIcon,
      title: '跟不上',
      desc: 'AI技术迭代飞快，竞争对手和兄弟单位已经开始用AI提效。再不上车，就要被甩开。',
      solution: '订阅即用，快速上线。同行已验证的成熟方案，可直接复用。'
    }
  ];

  const archLayers = [
    {
      num: '01',
      icon: BoltIcon,
      title: '算力机硬件 — AI的"发动机"',
      desc: '由我们提供的专用AI算力硬件，内置GPU算力，支持华为鲲鹏、海光等国产芯片。部署到您机房，插上电源和网络就能用，您无需单独采购。'
    },
    {
      num: '02',
      icon: CpuIcon,
      title: 'AI大模型 — AI的"大脑"',
      desc: '预装基座大模型+垂直小模型，上下文12万字，能读懂文本、能看图、能听音频视频。模型已调好，开机即用。'
    },
    {
      num: '03',
      icon: MonitorIcon,
      title: 'AI系统平台 — AI的"操作系统"',
      desc: '智能助手、知识库、文档处理、工作流引擎、用户管理……全员可用的AI工作平台，有界面、有权限、有协作。'
    },
    {
      num: '04',
      icon: SendIcon,
      title: '智能体应用 — AI的"即战力"',
      desc: '公文写作、合同审查、会议分析、简历筛选、发票对账、PPT制作、报告生成……20+种场景开箱即用，持续更新。'
    }
  ];

  const compareRows = [
    { label: '数据安全', self: { s: 'yes', t: '私有化部署' }, build: { s: 'yes', t: '安全' }, public: { s: 'no', t: '上传公网' } },
    { label: '落地周期', self: { s: 'yes', t: '当天可用' }, build: { s: 'no', t: '3-6个月' }, public: { s: 'yes', t: '即开即用' } },
    { label: '总体成本', self: { s: 'yes', t: '年度订阅' }, build: { s: 'no', t: '百万起步' }, public: { s: 'yes', t: '免费/低价' } },
    { label: '文件格式', self: { s: 'yes', t: '100+种，不限量' }, build: { s: 'dash', t: '需开发' }, public: { s: 'no', t: '10多种，限50文件' } },
    { label: '办公场景', self: { s: 'yes', t: '20+场景预装' }, build: { s: 'dash', t: '需开发' }, public: { s: 'no', t: '不贴业务' } },
    { label: '团队协作', self: { s: 'yes', t: '共享+权限' }, build: { s: 'dash', t: '需开发' }, public: { s: 'no', t: '不支持' } },
    { label: '成果交付', self: { s: 'yes', t: 'Excel/PDF/PPT' }, build: { s: 'dash', t: '需开发' }, public: { s: 'no', t: '仅文本' } },
    { label: '信创兼容', self: { s: 'yes', t: '鲲鹏/海光' }, build: { s: 'dash', t: '需适配' }, public: { s: 'no', t: '不支持' } },
    { label: '运维服务', self: { s: 'yes', t: '专业技术支撑' }, build: { s: 'no', t: '自建团队' }, public: { s: 'no', t: '无' } }
  ];

  useEffect(() => {
    fetchNews();
  }, []);

  useEffect(() => {
    const node = painGridRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPainInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const node = statsRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const fetchNews = async () => {
    try {
      const res = await newsAPI.getList({ limit: 4 });
      setNewsList(res.data || []);
    } catch (error) {
      console.error('获取新闻失败:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN');
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-gradient"></div>
          <div className="hero-pattern"></div>
        </div>
        <div className="container hero-content">
          <div className="hero-text">
            <h1 className="hero-title animate-fadeInUp">
              一款可信任的<span className="highlight">AI+</span>产品
            </h1>
            <p className="hero-subtitle animate-fadeInUp delay-1">
              本地化 AI 办公服务，在安全可控的环境中完成各类日常工作，让机构数据真正被理解、利用并转化为生产力。
            </p>
            <div className="hero-actions animate-fadeInUp delay-2">
              <Link to="/products" className="btn btn-primary btn-lg">
                探索产品功能
              </Link>
              <Link to="/contact" className="btn btn-secondary btn-lg">
                预约演示
              </Link>
            </div>
          </div>
          <div className="hero-visual animate-fadeInUp delay-3">
            <div className="browser-mockup">
              <img src="/chat-ui-demo.png" alt="AI智能助手界面" className="browser-screenshot" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-wrapper">
            <div className={`stats-left${statsInView ? ' in-view' : ''}`} ref={statsRef}>
              <div className="stats-glow"></div>
              <h2 className="stats-title">企业实力</h2>
              <div className="stats-grid">
                {productHighlights.map((item, index) => (
                  <div key={index} className="stat-item" style={{ transitionDelay: `${index * 0.1}s` }}>
                    <div className="stat-number">
                      <StatValue value={item.number} active={statsInView} />
                    </div>
                    <div className="stat-label">{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="stats-right">
              <div className="news-panel">
                <div className="news-panel-header">
                  <h3>最新消息</h3>
                  <Link to="/news" className="news-panel-more">更多 →</Link>
                </div>
                <div className="news-panel-list">
                  {newsList.length > 0 ? newsList.map((news, index) => (
                    <Link key={news._id} to={`/news/${news._id}`} className="news-panel-item">
                      <span className="news-panel-tag">{news.tag}</span>
                      <span className="news-panel-title">{news.title}</span>
                      <span className="news-panel-date">{formatDate(news.date)}</span>
                    </Link>
                  )) : (
                    <div className="news-panel-empty">暂无消息</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="credentials-section">
        <div className="container">
          <div className="section-header">
            <span className="section-eyebrow">WHY TRUST US</span>
            <h2 className="section-title">十余年深耕，最懂政企的AI团队</h2>
            <p className="section-subtitle">不是互联网公司转型做政企，而是从第一天起就在为您服务</p>
          </div>

          <div className="credentials-grid">
            {credentialCards.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className={`credential-card accent-${index % 4}`}>
                  <div className="credential-icon"><Icon /></div>
                  <h3 className="credential-title">{item.title}</h3>
                  <p className="credential-desc">{item.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="clients-block">
            <p className="clients-label">
              <CheckCircleIcon className="clients-label-icon" />
              他们已经在用微趣AI+
            </p>
            <div className="clients-row">
              {clientTags.map((tag, index) => (
                <span key={index} className="client-tag">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section className="pain-section">
        <div className="pain-pattern"></div>
        <div className="container">
          <span className="section-eyebrow section-eyebrow-dark">COMMON CONCERNS</span>
          <h2 className="section-title">您是不是也遇到了这四个问题？</h2>
          <p className="section-subtitle">很多单位想用AI，但一动手就卡住了。不是不想用，是确实有顾虑。</p>

          <div className={`pain-grid${painInView ? ' in-view' : ''}`} ref={painGridRef}>
            {painPoints.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className={`pain-item accent-${index % 4}`} style={{ transitionDelay: `${index * 0.12}s` }}>
                  <div className="pain-item-top">
                    <Icon className="pain-icon" />
                    <span className="pain-index">0{index + 1}</span>
                  </div>
                  <h3 className="pain-title">{item.title}</h3>
                  <p className="pain-desc">{item.desc}</p>
                  <p className="pain-solution">
                    <CheckCircleIcon className="pain-solution-icon" />
                    <span>{item.solution}</span>
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Architecture Section */}
      <section className="arch-section">
        <div className="container">
          <span className="section-eyebrow">PRODUCT ARCHITECTURE</span>
          <h2 className="section-title">一套标准产品，四层AI能力</h2>
          <p className="section-subtitle">就像订阅一个全能AI员工——算力硬件我们带过来部署好，开通即用</p>

          <div className="arch-stack">
            {archLayers.map((layer, index) => {
              const Icon = layer.icon;
              return (
                <div key={index} className="arch-layer">
                  <div className="arch-layer-marker">
                    <div className="arch-layer-num">{layer.num}</div>
                    {index < archLayers.length - 1 && <div className="arch-layer-connector"></div>}
                  </div>
                  <div className="arch-layer-body">
                    <h3 className="arch-layer-title">{layer.title}</h3>
                    <p className="arch-layer-desc">{layer.desc}</p>
                  </div>
                  <div className="arch-layer-icon"><Icon /></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-section">
        <div className="container">
          <span className="section-eyebrow">THE COMPARISON</span>
          <h2 className="section-title">三种AI路线，您选哪个？</h2>
          <p className="section-subtitle">很多单位想上AI，但不知道该走哪条路。帮您算清楚这笔账。</p>

          <div className="comparison-table">
            <div className="comparison-header comparison-header-3">
              <div className="comparison-col">对比维度</div>
              <div className="comparison-col">自建AI团队</div>
              <div className="comparison-col">公网大模型</div>
              <div className="comparison-col highlight-col">
                微趣AI+ <CheckCircleIcon className="col-badge-icon" />
              </div>
            </div>
            {compareRows.map((row, index) => (
              <div key={index} className="comparison-row comparison-row-3">
                <div className="comparison-col">{row.label}</div>
                <div className="comparison-col"><StatusIcon status={row.build.s} />{row.build.t}</div>
                <div className="comparison-col"><StatusIcon status={row.public.s} />{row.public.t}</div>
                <div className="comparison-col highlight-col"><StatusIcon status={row.self.s} />{row.self.t}</div>
              </div>
            ))}
          </div>

          <p className="compare-quote">自建太贵太慢，公网不安全不好用，标准化产品刚好。</p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-decoration"></div>
        <div className="container">
          <div className="cta-content">
            <h2>准备好让AI为您工作了吗？</h2>
            <p>订阅AI服务，开箱即用。我们提供算力硬件到您单位部署，让您的单位拥有自己的AI大脑。</p>
            <Link to="/contact" className="btn btn-primary btn-lg">
              预约产品演示
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;